import axios from './axiosConfig'

export type FinancialPaymentMethod =
 | 'cash'
 | 'card'
 | 'instapay'
 | 'electronic_wallet'
 | 'vodafone_cash'
 | 'bank_transfer'
 | 'payment_gateway'

export type FinancialAccountRow = {
 id: string
 legacy_vault_id?: string | null
 name: string
 account_type: string
 simple_type?: string
 payment_method: FinancialPaymentMethod
 provider_name?: string | null
 identifier_type?: string | null
 identifier_value?: string | null
 masked_identifier?: string | null
 currency: string
 branch_scope: 'selected_branches' | 'all_branches'
 is_active: boolean
 is_default_global: boolean
 is_default_for_branch?: boolean
 allow_negative: boolean
 allow_sales: boolean
 allow_refunds: boolean
 allow_expenses: boolean
 allow_payroll?: boolean
 allow_supplier_payments?: boolean
 allow_purchase_payments?: boolean
 allow_deposits: boolean
 allow_withdrawals: boolean
 allow_transfers: boolean
 allow_manual_adjustments: boolean
 balance: string
 current_balance?: string | null
 balance_difference?: string | null
 last_movement_at?: string | null
 notes?: string | null
 reconciliation?: {
  canonical_balance: string
  cached_balance: string | null
  vault_cached_balance: string | null
  difference: string | null
  has_warning: boolean
 }
 linked_vault?: {
  id: string
  name: string
  account_type?: string | null
  is_active: boolean
  cached_balance?: string | null
  is_physical_cash?: boolean
 } | null
 related_counts?: {
  sales: number | null
  refunds: number | null
  expenses: number | null
 }
 branch_links?: Array<{
  branch_id: string
  branch_name?: string | null
  is_active: boolean
  is_default: boolean
  display_order: number
 }>
}

export type PaymentSourceOperation =
 | 'sale_collection'
 | 'customer_collection'
 | 'layaway_collection'
 | 'customer_wallet_deposit'
 | 'customer_wallet_withdrawal'
 | 'expense'
 | 'payroll'
 | 'refund'
 | 'supplier_payment'
 | 'purchase_payment'
 | 'owner_drawing'
 | 'loan_repayment'
 | 'asset_purchase'
 | 'financial_withdrawal'
 | 'external_withdrawal'

export type PaymentSourceRow = {
 id: string
 selection_key?: string
 name: string
 simple_type: string
 simple_type_label: string
 payment_method: FinancialPaymentMethod
 branch_id?: string | null
 branch_name?: string | null
 balance?: string | null
 is_default: boolean
 is_cash: boolean
 linked_vault_id?: string | null
 is_available?: boolean
 unavailable_reason?: 'insufficient_balance' | 'inactive_vault' | string | null
}

export type FinancialAccountPayload = {
 name: string
 account_type: string
 payment_method: string
 provider_name?: string | null
 identifier_type?: string | null
 identifier_value?: string | null
 currency: string
 branch_scope: 'selected_branches' | 'all_branches'
 branch_ids: string[]
 default_branch_ids: string[]
 is_active: boolean
 is_default_global: boolean
 allow_negative: boolean
 allow_sales: boolean
 allow_refunds: boolean
 allow_expenses: boolean
 allow_payroll?: boolean
 allow_supplier_payments?: boolean
 allow_purchase_payments?: boolean
 allow_deposits: boolean
 allow_withdrawals: boolean
 allow_transfers: boolean
 allow_manual_adjustments: boolean
 notes?: string | null
 opening_balance?: number
 opening_balance_source?: string | null
 opening_branch_id?: string | null
}

export type ClassifiedMovementPayload = {
 branch_id?: string | null
 amount: number
 classification?: string
 notes?: string
 reference?: string
 occurred_at?: string
 idempotency_key: string
}

type ApiList = { status: string; data: FinancialAccountRow[]; message?: string }
type ApiOne = { status: string; data: FinancialAccountRow; message?: string }
type PaymentSourcesApiList = { status: string; data: PaymentSourceRow[]; message?: string }

export const financialAccountsAPI = {
 list: async (params?: Record<string, unknown>) =>
 (await axios.get<ApiList>('/financial-accounts', { params })).data,
 get: async (id: string) =>
 (await axios.get<ApiOne>(`/financial-accounts/${id}`)).data,
 revealIdentifier: async (id: string) =>
 (await axios.post<{ status: string; data: { identifier_value: string | null; masked_identifier?: string | null; identifier_type?: string | null } }>(
  `/financial-accounts/${id}/reveal-identifier`,
 )).data,
 available: async (branchId: string, capability = 'sales', paymentMethod?: string) =>
 (await axios.get<ApiList>('/financial-accounts/available', {
  params: {
   branch_id: branchId,
   capability,
   ...(paymentMethod ? { payment_method: paymentMethod } : {}),
  },
 })).data,
 paymentSources: async (params: {
  operation: PaymentSourceOperation
  branch_id?: string | null
  amount?: string | number | null
  original_financial_account_id?: string | null
  include_unavailable?: boolean
 }) => (await axios.get<PaymentSourcesApiList>('/financial-accounts/payment-sources', {
   params: {
    operation: params.operation,
    ...(params.branch_id ? { branch_id: params.branch_id } : {}),
   ...(params.amount != null && String(params.amount) !== '' ? { amount: params.amount } : {}),
   ...(params.original_financial_account_id ? { original_financial_account_id: params.original_financial_account_id } : {}),
   ...(params.include_unavailable ? { include_unavailable: 1 } : {}),
  },
 })).data,
 create: async (payload: FinancialAccountPayload) =>
 (await axios.post<ApiOne>('/financial-accounts', payload)).data,
 createSimpleCash: async (payload: {
  name: string
  branch_id?: string | null
  opening_balance?: number
  use_as_pos_drawer?: boolean
  is_default?: boolean
  is_active?: boolean
  notes?: string | null
 }) => (await axios.post<ApiOne>('/financial-accounts/simple-cash', payload)).data,
 createSimplePayment: async (payload: {
  name: string
  simple_payment_method:
   | 'card'
   | 'instapay'
   | 'electronic_wallet'
   | 'bank_transfer'
   | 'payment_gateway'
  available_for_all_branches?: boolean
  is_unlinked_main_account?: boolean
  branch_ids?: string[]
  opening_balance?: number
  is_default?: boolean
  is_active?: boolean
  notes?: string | null
 }) => (await axios.post<ApiOne>('/financial-accounts/simple-payment', payload)).data,
 update: async (id: string, payload: FinancialAccountPayload) =>
 (await axios.put<ApiOne>(`/financial-accounts/${id}`, payload)).data,
 activate: async (id: string) =>
 (await axios.post<ApiOne>(`/financial-accounts/${id}/activate`)).data,
 deactivate: async (id: string) =>
 (await axios.post<ApiOne>(`/financial-accounts/${id}/deactivate`)).data,
 transactions: async (id: string, params?: Record<string, unknown>) =>
 (await axios.get(`/financial-accounts/${id}/transactions`, { params })).data,
 deposit: async (id: string, payload: ClassifiedMovementPayload) =>
 (await axios.post(`/financial-accounts/${id}/deposit`, payload)).data,
 withdraw: async (id: string, payload: ClassifiedMovementPayload) =>
 (await axios.post(`/financial-accounts/${id}/withdraw`, payload)).data,
 transfer: async (payload: {
  from_financial_account_id: string
  to_financial_account_id: string
  branch_id: string
  amount: number
  reference?: string
  idempotency_key: string
 }) => (await axios.post('/financial-accounts-transfer', payload)).data,
 adjust: async (
  id: string,
  payload: {
   branch_id: string
   direction: 'in' | 'out'
   amount: number
   reason: string
   idempotency_key: string
  },
 ) => (await axios.post(`/financial-accounts/${id}/adjust`, payload)).data,
 settleCard: async (payload: {
  branch_id: string
  clearing_financial_account_id: string
  bank_financial_account_id: string
  fee_financial_account_id?: string
  gross_amount: number
  fee_amount?: number
  difference_amount?: number
  net_bank_amount?: number
  settlement_reference: string
  settlement_date: string
  notes?: string
  idempotency_key: string
 }) => (await axios.post('/card-settlements', payload)).data,
 listCardSettlements: async (params?: Record<string, unknown>) =>
  (await axios.get('/card-settlements', { params })).data,
}
